import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { proceedingParticipantsApi } from "@/api";
import { deactivateParticipantValidationSchema } from "@/pages/system-settings/tabs/proceeding-participants/components/edit-participant/deactivate-participant-dialog/DeactivateParticipantDialog.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export interface DeactivateParticipantFormType {
  reason: string;
}

export function useParticipantDeactivate(participantId: string, onSuccess: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const deactivateMutation = useMutation({
    mutationFn: (reason: string) =>
      proceedingParticipantsApi.deactivateProceedingParticipant({
        participantId,
        deactivateProceedingParticipantRequest: { reason },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ProceedingParticipants] });
      showSuccess(t("proceedingParticipants.deactivate.successfullyDeactivated"));
      onSuccess();
    },
  });

  const validationSchema = useMemo(() => deactivateParticipantValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<DeactivateParticipantFormType>({
    initialValues: { reason: "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await deactivateMutation.mutateAsync(values.reason.trim());
    },
  });

  return { formik };
}
